import { useEffect, useRef, useState } from "react";

/**
 * useReveal – attaches an IntersectionObserver to fade-up elements when scrolled into view.
 * Usage:
 *   const { ref, visible } = useReveal();
 *   <div ref={ref} className={visible ? "is-revealed" : "is-hidden"}>...</div>
 *
 * Or use the SectionReveal wrapper.
 */
export const useReveal = (options = {}) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!ref.current) return;
        const node = ref.current;
        if (typeof IntersectionObserver === "undefined") {
            setVisible(true);
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.unobserve(node);
                }
            },
            { threshold: 0.12, rootMargin: "0px 0px -10% 0px", ...options }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return { ref, visible };
};

/**
 * Wrap any element to make it fade-up on scroll-in.
 * Children retain their layout (uses display: contents-style passthrough).
 */
export const Reveal = ({ children, delay = 0, className = "" }) => {
    const { ref, visible } = useReveal();
    return (
        <div
            ref={ref}
            className={`transition-all duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${className} ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
            style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
        >
            {children}
        </div>
    );
};
